import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import './MainContent.css';
import headerBg from '../assets/images/header-bg.jpg';
import logoImage from '../assets/images/logo.png';
import { useTranslation } from 'react-i18next';
import TopBar from './TopBar';
import SongDetail from './SongDetail';
import SongCard from './SongCard/SongCard';
import ArtistsPage from './ArtistsPage/ArtistsPage';
import Footer from './Footer/Footer';
import PlaylistsView from './PlaylistsView/PlaylistsView';
import useAnimatedCounter from '../hooks/useAnimatedCounter';
import InfoPage from './InfoPage/InfoPage';

function importAll(r) { let images = {}; r.keys().forEach((item) => { images[item.replace('./', '')] = r(item); }); return images; }
const images = importAll(require.context('../assets/images', false, /\.(png|jpe?g|svg)$/));

function MainContent({ 
    songs, activeSong, selectedSong, onSongSelect, onBackClick, onPlayClick, onPlayPlaylist, 
    onMainPlay, isPlaying, currentView, onNavigate, searchQuery, onSearchChange, togglePlayPause, totalListenCount
}) {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState('grid');
  const animatedTotalCount = useAnimatedCounter(totalListenCount, 1500);

  const headerRef = useRef(null);

  // THE DEFINITIVE FIX: This hook runs before the browser can paint the screen.
  // It scrolls the entire window to the top whenever the view changes.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView, selectedSong]);


  const handleInlinePlay = (e, index) => { e.stopPropagation(); onPlayClick(index); };

  const filteredSongs = useMemo(() => {
    if (!searchQuery) { return songs; }
    return songs.filter(song =>
      song.title.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      song.title.ja.toLowerCase().includes(searchQuery.toLowerCase()) ||
      song.artist.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      song.artist.ja.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [songs, searchQuery]);

  const handleHeaderMouseMove = (e) => {
    const header = headerRef.current;
    if (!header) return;
    const { left, top, width, height } = header.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    
    const posX = (x / width - 0.5) * 2;
    const posY = (y / height - 0.5) * 2;
    
    header.style.backgroundPosition = `${50 - posX * 3}% ${50 - posY * 3}%`;
  };

  const handleHeaderMouseLeave = () => {
    const header = headerRef.current;
    if (!header) return;
    header.style.backgroundPosition = 'center';
  };
  
  const AnimatedHeaderText = useMemo(() => {
    const text = t('appName');
    return text.split('').map((char, index) => (
      <span key={index} style={{ animationDelay: `${index * 50}ms` }}>
        {char === ' ' ? '\u00A0' : char}
      </span>
    ));
  }, [t]);

  let viewContent;
  if (currentView === 'artists') {
    viewContent = <ArtistsPage />;
  } else if (currentView === 'about') {
    viewContent = (
      <InfoPage title={t('aboutTitle')}>
        <h2>{t('aboutHeader')}</h2>
        <p>{t('aboutText')}</p>
      </InfoPage>
    );
  } else if (currentView === 'contact') {
    viewContent = (
      <InfoPage title={t('contactTitle')}>
        <h2>{t('contactHeader')}</h2>
        <p>{t('contactText')}</p>
        <p><a href="mailto:contact@aoitsukimusic.com">contact@aoitsukimusic.com</a></p>
      </InfoPage>
    );
  } else if (currentView === 'privacy') {
    viewContent = (
      <InfoPage title={t('privacyTitle')}>
        <h2>{t('privacyHeader')}</h2>
        <p>{t('privacyIntro')}</p>
        <p>{t('privacyConsent')}</p>
        <h2>{t('privacyAdHeader')}</h2>
        <p>{t('privacyAdP1')}</p>
        <p>{t('privacyAdP2')}</p>
        <h2>{t('privacyOptOutHeader')}</h2>
        <p>{t('privacyOptOutP1')}</p>
        <p><a href="https://adssettings.google.com/authenticated" target="_blank" rel="noopener noreferrer">{t('privacyOptOutLink')}</a></p>
        <p>{t('privacyMoreInfo')}</p>
      </InfoPage>
    );
  } else {
    viewContent = (
      <div className="main-content">
        <div 
          className="artist-header" 
          style={{ backgroundImage: `url(${headerBg})` }}
          ref={headerRef}
          onMouseMove={handleHeaderMouseMove}
          onMouseLeave={handleHeaderMouseLeave}
        >
            <a href="https://www.youtube.com/@AoiTsukiMusic" target="_blank" rel="noopener noreferrer" className="youtube-logo-link">
                <img src={logoImage} alt="YouTube Channel Logo" />
            </a>
            <div className="artist-header-content">
                <h1>{AnimatedHeaderText}</h1>
                <p className="total-listeners">{animatedTotalCount.toLocaleString()} {t('totalPlays')}</p>
            </div>
        </div>
        <PlaylistsView 
          onPlayPlaylist={onPlayPlaylist} 
          activeSong={activeSong} 
          isPlaying={isPlaying} 
          togglePlayPause={togglePlayPause} 
        />
        <div className="artist-body">
          <div className="controls"><button className="play-button" onClick={onMainPlay}><i className={isPlaying ? "fas fa-pause" : "fas fa-play"}></i></button></div>
          <div className="view-header"><h2>{t('popular')}</h2><div className="view-toggle-buttons"><button className={`toggle-button ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}><i className="fas fa-list"></i></button><button className={`toggle-button ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}><i className="fas fa-th-large"></i></button></div></div>
          {viewMode === 'list' ? (
            <div className="song-list">{filteredSongs.map((song, index) => { 
              const isThisSongPlaying = isPlaying && activeSong && activeSong.id === song.id; 
              return ( <div className="song-row" key={song.id} onClick={() => onSongSelect(song)} style={{ animationDelay: `${index * 30}ms` }}> <div className="song-index-container"><span className="song-index-number">{index + 1}</span></div> <div className="song-art-container"><img src={images[song.imageSrc]} alt={song.title[i18n.language] || ''} className="song-row-art" /><button className="inline-play-button" onClick={(e) => handleInlinePlay(e, index)}><i className={isThisSongPlaying ? "fas fa-pause" : "fas fa-play"}></i></button></div> <div className="song-details"><span className="song-title">{song.title[i18n.language] || ''}</span><span className="song-artist">{song.artist[i18n.language] || ''}</span></div> <div className="song-streams">{song.streamCount}</div> <div className="song-duration">{song.duration}</div> </div> ); 
            })}</div>
          ) : (
            <div className="song-grid">{filteredSongs.map((song, index) => (
              <SongCard key={song.id} song={song} onCardClick={() => onSongSelect(song)} onPlayClick={() => onPlayClick(index)} isPlaying={isPlaying} isCurrent={activeSong && activeSong.id === song.id} style={{ animationDelay: `${index * 30}ms` }} />
            ))}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="main-content-container">
      <TopBar onNavigate={onNavigate} searchQuery={searchQuery} onSearchChange={onSearchChange} songs={songs} onSongSelect={onSongSelect} />
      <div className="scrollable-content">
        {selectedSong ? (
          <SongDetail song={selectedSong} onBackClick={onBackClick} onPlayClick={() => onPlayClick(songs.findIndex(s => s.id === selectedSong.id))} isPlaying={isPlaying} isCurrent={activeSong && activeSong.id === selectedSong.id} />
        ) : (
          <> {viewContent} <Footer onNavigate={onNavigate} /> </>
        )}
      </div>
    </div>
  );
}

export default MainContent;